// shared/presentation/outcome-presentation.ts — the canonical status -> {cue,label}
// registries consumed by the investigation monitor, the chunk inspector (spec 217, C6)
// and, since spec 498, the source-inspector family. Covers the union of the monitor and
// inspector status sets (FR-008); no surface keeps an else/default fallback glyph.
//
// Ownership split (spec 498 FR-006): these registries own `label` and `cue`. The
// `variant` a caller hands to StatusIconComponent stays with the caller — it is an
// icon-rendering concern the registry has never modelled, and pulling it in would give
// this module a second responsibility.

/**
 * Closed set of run/task outcomes across the monitor + inspector surfaces.
 * Terminal-success synonyms (`complete`/`done`/`succeeded`) all resolve to the same cue.
 */
export type RunTaskOutcome =
  | 'draft'
  | 'queued'
  | 'running'
  | 'active' // non-terminal (union of monitor + inspector sets)
  | 'paused'
  | 'superseded'
  | 'complete'
  | 'done'
  | 'succeeded' // terminal success synonyms
  | 'failed' // terminal failure
  | 'cancelled'; // terminal cancel

export interface OutcomePresentation {
  /** Single glyph cue: success '✓', failed '✕', cancelled '⊘', running '◐', ... */
  readonly cue: string;
  /** Human-readable label. */
  readonly label: string;
  // Surface-specific visual bindings (pill class, banner variant) MAY layer on top,
  // but cue + label are resolved ONLY from this registry.
}

/**
 * The single canonical outcome → {cue,label} registry. Exactly one entry per
 * RunTaskOutcome (SC-007). Glyph invariants (research D3, flagged): terminal-success
 * synonyms `complete`/`done`/`succeeded` share `✓`, `failed` is `✕`, and `cancelled` is
 * `⊘` (the inspector's glyph, replacing the monitor's former `✕` for cancelled — the one
 * flagged user-visible reconciliation). Non-terminal cues carry the newer inspector
 * treatment (`◐` running). `satisfies Record<...>` makes an omitted outcome a compile error.
 */
export const OUTCOME_PRESENTATION = {
  draft: { cue: '○', label: 'Draft' },
  queued: { cue: '◌', label: 'Queued' },
  running: { cue: '◐', label: 'Running' },
  active: { cue: '◐', label: 'Active' },
  paused: { cue: '‖', label: 'Paused' },
  superseded: { cue: '⟳', label: 'Superseded' },
  complete: { cue: '✓', label: 'Complete' },
  done: { cue: '✓', label: 'Done' },
  succeeded: { cue: '✓', label: 'Succeeded' },
  failed: { cue: '✕', label: 'Failed' },
  cancelled: { cue: '⊘', label: 'Cancelled' },
} as const satisfies Record<RunTaskOutcome, OutcomePresentation>;

/**
 * Spec 498 (US2, FR-006) — the source-preparation lifecycle, a SIBLING closed
 * set rather than more members of {@link RunTaskOutcome}.
 *
 * The two token sets barely intersect: this one has `pending`, `completed` and
 * `partial`, which RunTaskOutcome does not, and RunTaskOutcome has `draft`,
 * `queued` and `superseded`, which this one does not. The L0 stage tokens
 * (`ok`/`warn`/`error`/`in_progress`/`skipped`) have no counterpart at all.
 * Folding them together would make a run/task vocabulary carry source states
 * for its five existing consumers.
 *
 * What the two registries DO share is the cue vocabulary — one glyph decides how
 * "finished" looks, across both token sets. That is the property spec 217
 * actually wanted, and `outcome-parity.spec.ts` asserts it by comparing the two
 * tables rather than by restating the glyph.
 */
export type SourceLifecycleOutcome =
  // the source's own preparation state
  | 'pending'
  | 'running'
  | 'completed'
  | 'partial'
  | 'failed'
  | 'cancelled'
  // one L0 stage's state
  | 'ok'
  | 'warn'
  | 'error'
  | 'in_progress'
  | 'skipped'
  // absence of an L0 run, or of the detail payload describing one
  | 'no_run'
  | 'detail_unavailable';

/**
 * The source-preparation lifecycle → {cue,label} registry. Words are UNCHANGED
 * from what the source-inspector surfaces rendered before this spec — FR-006 is
 * a re-homing of authorship, not a copy revision, so `completed` and `ok` both
 * keep `Completed` even though the sibling registry calls that outcome `Done`.
 */
export const SOURCE_LIFECYCLE_PRESENTATION = {
  pending: { cue: '◌', label: 'Pending' },
  running: { cue: '◐', label: 'Running' },
  completed: { cue: '✓', label: 'Completed' },
  partial: { cue: '◑', label: 'Partial' },
  failed: { cue: '✕', label: 'Failed' },
  cancelled: { cue: '⊘', label: 'Cancelled' },
  ok: { cue: '✓', label: 'Completed' },
  warn: { cue: '⚠', label: 'Warning' },
  error: { cue: '✕', label: 'Failed' },
  in_progress: { cue: '◐', label: 'In progress' },
  skipped: { cue: '⊘', label: 'Skipped' },
  no_run: { cue: '○', label: 'No run' },
  detail_unavailable: { cue: '○', label: 'Detail unavailable' },
} as const satisfies Record<SourceLifecycleOutcome, OutcomePresentation>;

/**
 * The terminal-success tokens of each registry, named rather than inferred.
 *
 * These two lists are what the cross-registry cue test compares. Naming them
 * here — beside the tables — is what lets that test assert "one glyph" by
 * reading the tables instead of by restating `'✓'`, which would pass even if
 * both tables drifted together.
 */
export const TERMINAL_SUCCESS_OUTCOMES = ['complete', 'done', 'succeeded'] as const satisfies
  readonly RunTaskOutcome[];

export const TERMINAL_SUCCESS_SOURCE_OUTCOMES = ['completed', 'ok'] as const satisfies
  readonly SourceLifecycleOutcome[];
